import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props { children: ReactNode }
interface State { error: Error | null; info: ErrorInfo | null }

/**
 * Zachytí výjimky při renderu (jinak by spadla celá appka na bílou obrazovku)
 * a vypíše konkrétní chybu + stack. Nabídne vymazání uloženého stavu a odhlášení,
 * protože častou příčinou je poškozený `user`/`token` v localStorage.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Vypíše do konzole (F12) celý stack
    console.error('[ErrorBoundary]', error, info)
    this.setState({ info })
  }

  private reset = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    window.location.href = '/'
  }

  render() {
    const { error, info } = this.state
    if (!error) return this.props.children

    return (
      <div style={{ minHeight: '100vh', background: '#0b1020', color: '#e5e7eb', padding: '32px', fontFamily: 'ui-monospace, monospace' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h1 style={{ color: '#f87171', fontSize: 22, marginBottom: 8 }}>⚠ Aplikace spadla při renderu</h1>
          <p style={{ color: '#9ca3af', marginBottom: 16 }}>Tahle obrazovka nahrazuje bílou stránku, ať vidíme příčinu.</p>

          <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 6 }}>{error.name}: {error.message}</div>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#cbd5e1', overflow: 'auto', maxHeight: 280 }}>
{error.stack}
            </pre>
          </div>

          {info?.componentStack && (
            <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ color: '#60a5fa', fontWeight: 700, marginBottom: 6 }}>Component stack</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#cbd5e1', overflow: 'auto', maxHeight: 220 }}>
{info.componentStack}
              </pre>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={this.reset} style={{ background: '#4f46e5', color: '#fff', border: 0, borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 600 }}>
              Vymazat data a odhlásit
            </button>
            <button onClick={() => window.location.reload()} style={{ background: '#374151', color: '#fff', border: 0, borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 600 }}>
              Zkusit znovu
            </button>
          </div>
        </div>
      </div>
    )
  }
}
